import type { MayBe } from 'types/common';
import type { KioskModel, KioskType } from 'types/KioskTypes';
import type { Dealer } from 'modules/dealers/types/ContextResponse';

export enum ScannerConnectionFailureReason {
  // `navigator.hid` missing: not Edge/Chrome, or page not served in HTTPS
  WEBHID_UNAVAILABLE = 'WEBHID_UNAVAILABLE',
  // No HID device allowed: Edge policy `WebHidAllowDevicesForUrls` missing, or nothing plugged in
  NO_ALLOWED_HID_DEVICE = 'NO_ALLOWED_HID_DEVICE',
  // HID devices allowed, but none from the scanner vendor
  SCANNER_VENDOR_NOT_FOUND = 'SCANNER_VENDOR_NOT_FOUND',
  // Several interfaces from the scanner vendor, none of them HID POS
  NO_HID_POS_INTERFACE = 'NO_HID_POS_INTERFACE',
  // Scanner found, but `device.open()` threw
  OPEN_FAILED = 'OPEN_FAILED',
  // `navigator.hid.getDevices()` threw (e.g. SecurityError when the "hid" permissions policy blocks it)
  GET_DEVICES_FAILED = 'GET_DEVICES_FAILED',
  // Any other error thrown while connecting: caught so the retry loop never dies
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
}

export type ScannerFailurePolicy = {
  // An error (not a "not there yet" state): reported without waiting for the boot grace period
  isReportedImmediately: boolean;
  // Retrying cannot fix it: the connection loop stops
  isPermanent: boolean;
};

export type ScannerKioskContext = {
  kioskId: MayBe<string>;
  kioskName: MayBe<string>;
  kioskType: MayBe<KioskType>;
  kioskModel: MayBe<KioskModel>;
  dealers: Pick<Dealer, 'id' | 'dealerName'>[];
};

export type ScannerFound = {
  device: HIDDevice;
};

export type ScannerNotFound = {
  reason: ScannerConnectionFailureReason;
};

export type ScannerLookup = ScannerFound | ScannerNotFound;

export type QrCodeListener = (qrCodeData: string) => void;

export type ScannerFailure = {
  reason: ScannerConnectionFailureReason;
  devices: HIDDevice[];
  attempts: number;
  kioskContext: MayBe<ScannerKioskContext>;
  error?: unknown;
};

// What made an attempt fail: the attempt count and the kiosk info are added by the scanner
export type ScannerFailureCause = Pick<ScannerFailure, 'reason' | 'error'> & {
  devices?: HIDDevice[];
};

// Dealers stringified: Sentry `normalizeDepth` would truncate the nested objects
export type SerializedKioskContext = Omit<ScannerKioskContext, 'dealers'> & {
  dealers: string;
};

export type QrCodeScannerSentryContext = {
  reason: ScannerConnectionFailureReason;
  attempts: number;
  expectedVendorId: number;
  expectedUsagePage: number;
  error: MayBe<string>;
  // Stringified: Sentry `normalizeDepth` would truncate the nested HID collections
  hidDevices: string;
  userAgent: string;
  url: string;
};

export type ScannerFailureContexts = {
  kiosk: MayBe<SerializedKioskContext>;
  qrCodeScanner: QrCodeScannerSentryContext;
};

export type ScannerFailureTags = {
  qr_scanner_failure_reason: ScannerConnectionFailureReason;
};

export type ScannerFailureCaptureContext = {
  tags: ScannerFailureTags;
  contexts: ScannerFailureContexts;
};

export type ScannerFailureReport = {
  sentryError: Error;
  captureContext: ScannerFailureCaptureContext;
};
